export type TenantId =
  | 'axa-integral'
  | 'axa-prevention'
  | 'axa-prevention-v2'
  | 'demo'
  | 'ernest-pro'
  | 'esprit-retraite'
  | 'ircem'
  | 'klesia'
  | 'mh'
  | 'umen'

export type TenantConfig = {
  appShellClassName: string
  badgeClassName: string
  cardClassName: string
  conversationHeaderClassName: string
  conversationHeaderTitleClassName: string
  headerIconButtonClassName: string
  id: TenantId
  label: string
  themeStyle: Record<string, string>
  webhookUrl?: string
}

const TENANT_CONFIGS: Record<TenantId, TenantConfig> = {
  'axa-integral': {
    id: 'axa-integral',
    label: 'AXA Intégrale',
    appShellClassName: 'text-[#051C38] dark:bg-[#031428] dark:text-slate-100',
    badgeClassName: 'text-[#051C38] dark:text-slate-200',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#051C38]/12 dark:ring-0 md:dark:ring-[#051C38]/30',
    conversationHeaderClassName: 'ernest-pro-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-white',
    headerIconButtonClassName: 'ernest-pro-header-icon-button',
    themeStyle: {
      '--color-background': '#f5f7fa',
      '--color-background-muted': '#e9eef4',
      '--color-foreground': '#051c38',
      '--color-primary': '#051c38',
      '--color-primary-base': '#051c38',
      '--color-primary-gradient':
        'linear-gradient(135deg, #051c38 0%, #0d3158 52%, #051c38 100%)',
      '--color-primary-hover': '#031428',
      '--color-primary-hover-gradient': '#031428',
      '--color-ring': '#051c38',
      background: 'linear-gradient(180deg, #f6f8fb 0%, #eef2f7 50%, #f8fafc 100%)',
    },
  },
  'axa-prevention': {
    id: 'axa-prevention',
    label: 'AXA Prévention',
    appShellClassName: 'text-[#051C38] dark:bg-[#031428] dark:text-slate-100',
    badgeClassName: 'text-[#051C38] dark:text-slate-200',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#051C38]/12 dark:ring-0 md:dark:ring-[#051C38]/30',
    conversationHeaderClassName: 'ernest-pro-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-white',
    headerIconButtonClassName: 'ernest-pro-header-icon-button',
    themeStyle: {
      '--color-background': '#f5f7fa',
      '--color-background-muted': '#e9eef4',
      '--color-foreground': '#051c38',
      '--color-primary': '#051c38',
      '--color-primary-base': '#051c38',
      '--color-primary-gradient':
        'linear-gradient(135deg, #051c38 0%, #0d3158 52%, #051c38 100%)',
      '--color-primary-hover': '#031428',
      '--color-primary-hover-gradient': '#031428',
      '--color-ring': '#051c38',
      background: 'linear-gradient(180deg, #f6f8fb 0%, #eef2f7 50%, #f8fafc 100%)',
    },
  },
  'axa-prevention-v2': {
    id: 'axa-prevention-v2',
    label: 'AXA Prévention v2',
    appShellClassName: 'text-[#051C38] dark:bg-[#031428] dark:text-slate-100',
    badgeClassName: 'text-[#051C38] dark:text-slate-200',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#051C38]/12 dark:ring-0 md:dark:ring-[#051C38]/30',
    conversationHeaderClassName: 'ernest-pro-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-white',
    headerIconButtonClassName: 'ernest-pro-header-icon-button',
    themeStyle: {
      '--color-background': '#f5f7fa',
      '--color-background-muted': '#e9eef4',
      '--color-foreground': '#051c38',
      '--color-primary': '#051c38',
      '--color-primary-base': '#051c38',
      '--color-primary-gradient':
        'linear-gradient(135deg, #051c38 0%, #0d3158 52%, #051c38 100%)',
      '--color-primary-hover': '#031428',
      '--color-primary-hover-gradient': '#031428',
      '--color-ring': '#051c38',
      background: 'linear-gradient(180deg, #f6f8fb 0%, #eef2f7 50%, #f8fafc 100%)',
    },
  },
  demo: {
    id: 'demo',
    label: 'Demo',
    appShellClassName: 'bg-blue-50 text-blue-900 dark:bg-[#0f172a] dark:text-blue-100',
    badgeClassName: 'text-blue-600 dark:text-blue-300',
    cardClassName:
      'ring-0 md:ring-2 md:ring-blue-200/70 dark:ring-0 md:dark:ring-blue-500/30',
    conversationHeaderClassName: 'demo-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-white',
    headerIconButtonClassName: 'demo-header-icon-button',
    themeStyle: {
      '--color-background': '#eff6ff',
      '--color-background-muted': '#dbeafe',
      '--color-foreground': '#1e3a8a',
      '--color-primary': '#3b82f6',
      '--color-primary-base': '#3b82f6',
      '--color-primary-gradient': '#3b82f6',
      '--color-primary-hover': '#164e9c',
      '--color-primary-hover-gradient': '#164e9c',
      '--color-ring': '#3b82f6',
    },
  },
  klesia: {
    id: 'klesia',
    label: 'Klesia',
    appShellClassName: 'text-[#213067] dark:bg-[#213067] dark:text-[#EAF2FF]',
    badgeClassName: 'text-[#D57A36] dark:text-[#FFD2AE]',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#B8E1F1] dark:ring-0 md:dark:ring-[#D57A36]/40',
    conversationHeaderClassName: 'klesia-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-[#213067]',
    headerIconButtonClassName: 'klesia-header-icon-button',
    themeStyle: {
      '--color-background': '#eef9fc',
      '--color-background-muted': '#dceef7',
      '--color-foreground': '#213067',
      '--color-primary': '#d57a36',
      '--color-primary-base': '#d57a36',
      '--color-primary-gradient': '#d57a36',
      '--color-primary-hover': '#b86428',
      '--color-primary-hover-gradient': '#b86428',
      '--color-ring': '#d57a36',
      '--color-tertiary-base': '#798ac8',
      background: 'linear-gradient(180deg, #eef9fc 0%, #e8f6fb 55%, #f5fafd 100%)',
    },
  },
  mh: {
    id: 'mh',
    label: 'MH',
    appShellClassName: 'bg-[#FFF2F0] text-[#7A2015] dark:bg-[#2A1512] dark:text-[#FFE8E4]',
    badgeClassName: 'text-[#E2250C] dark:text-[#FFB0A4]',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#E2250C]/25 dark:ring-0 md:dark:ring-[#E2250C]/40',
    conversationHeaderClassName: 'mh-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-[#F8FAFC]',
    headerIconButtonClassName: 'mh-header-icon-button',
    themeStyle: {
      '--color-background': '#fff2f0',
      '--color-background-muted': '#ffe8e4',
      '--color-foreground': '#7a2015',
      '--color-primary': '#e2250c',
      '--color-primary-base': '#e2250c',
      '--color-primary-gradient': '#e2250c',
      '--color-primary-hover': '#b71e0a',
      '--color-primary-hover-gradient': '#b71e0a',
      '--color-ring': '#ffb0a4',
    },
  },
  ircem: {
    id: 'ircem',
    label: 'IRCEM',
    appShellClassName: 'text-[#4c0519] dark:bg-[#1a0a10] dark:text-rose-100',
    badgeClassName: 'text-[#D51130] dark:text-[#ff8fa8]',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#e6007a]/20 dark:ring-0 md:dark:ring-[#D51130]/35',
    conversationHeaderClassName: 'ircem-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-[#F8FAFC]',
    headerIconButtonClassName: 'ircem-header-icon-button',
    themeStyle: {
      '--color-background': '#fff8fa',
      '--color-background-muted': '#ffeef3',
      '--color-foreground': '#4c0519',
      '--color-primary': '#43ac39',
      '--color-primary-base': '#d51130',
      '--color-primary-gradient':
        'linear-gradient(45deg, rgb(230, 0, 122) 0%, rgb(213, 17, 48) 45%, rgb(213, 17, 48) 100%)',
      '--color-primary-hover': '#389732',
      '--color-primary-hover-gradient': '#389732',
      '--color-ring': '#43ac39',
      background:
        'linear-gradient(160deg, rgba(230, 0, 122, 0.09) 0%, rgba(213, 17, 48, 0.06) 45%, #fff8fa 100%)',
    },
  },
  'ernest-pro': {
    id: 'ernest-pro',
    label: 'Ernest Pro',
    appShellClassName: 'text-[#051C38] dark:bg-[#031428] dark:text-slate-100',
    badgeClassName: 'text-[#051C38] dark:text-slate-200',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#051C38]/12 dark:ring-0 md:dark:ring-[#051C38]/30',
    conversationHeaderClassName: 'ernest-pro-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-white',
    headerIconButtonClassName: 'ernest-pro-header-icon-button',
    themeStyle: {
      '--color-background': '#f5f7fa',
      '--color-background-muted': '#e9eef4',
      '--color-foreground': '#051c38',
      '--color-primary': '#051c38',
      '--color-primary-base': '#051c38',
      '--color-primary-gradient':
        'linear-gradient(135deg, #051c38 0%, #0d3158 52%, #051c38 100%)',
      '--color-primary-hover': '#031428',
      '--color-primary-hover-gradient': '#031428',
      '--color-ring': '#051c38',
      background: 'linear-gradient(180deg, #f6f8fb 0%, #eef2f7 50%, #f8fafc 100%)',
    },
  },
  'esprit-retraite': {
    id: 'esprit-retraite',
    label: 'Esprit Retraite',
    appShellClassName: 'text-[#006b7d] dark:bg-[#0a1a22] dark:text-cyan-100',
    badgeClassName: 'text-[#00afcb] dark:text-[#7ee8f5]',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#00afcb]/25 dark:ring-0 md:dark:ring-[#00afcb]/35',
    conversationHeaderClassName: 'esprit-retraite-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-[#F8FAFC]',
    headerIconButtonClassName: 'esprit-retraite-header-icon-button',
    themeStyle: {
      '--color-background': '#f4fbe8',
      '--color-background-muted': '#e8f7d6',
      '--color-foreground': '#006b7d',
      '--color-primary': '#d2d100',
      '--color-primary-base': '#00afcb',
      '--color-primary-gradient': '#d2d100',
      '--color-primary-hover': '#b8bc00',
      '--color-primary-hover-gradient': '#b8bc00',
      '--color-primary-foreground': '#1a3d00',
      '--color-ring': '#d2d100',
      background: 'linear-gradient(180deg, #f4fbe8 0%, #ecfeff 50%, #f7fce8 100%)',
    },
  },
  umen: {
    id: 'umen',
    label: 'Umen',
    appShellClassName: 'text-[#005a75] dark:bg-[#002633] dark:text-sky-100',
    badgeClassName: 'text-[#00ABDF] dark:text-[#7dd8f5]',
    cardClassName: 'ring-0 md:ring-2 md:ring-[#00ABDF]/25 dark:ring-0 md:dark:ring-[#00ABDF]/40',
    conversationHeaderClassName: 'umen-chat-sticky-header',
    conversationHeaderTitleClassName: 'text-[#F8FAFC]',
    headerIconButtonClassName: 'umen-header-icon-button',
    themeStyle: {
      '--color-background': '#f0f9fc',
      '--color-background-muted': '#e0f4fa',
      '--color-foreground': '#005a75',
      '--color-primary': '#000000',
      '--color-primary-base': '#00ABDF',
      '--color-primary-gradient': '#000000',
      '--color-primary-hover': '#1a1a1a',
      '--color-primary-hover-gradient': '#1a1a1a',
      '--color-primary-foreground': '#FFFFFF',
      '--color-ring': '#00ABDF',
      background: 'linear-gradient(180deg, #f0f9fc 0%, #e8f6fb 55%, #f5fafd 100%)',
    },
  },
}

export const DEFAULT_TENANT: TenantId = 'demo'

export const TENANT_STORAGE_KEY = 'ernest-tenant-id'

/** Set on the incoming request by consumer-web `proxy.ts` for SSR tenant (host / ?tenant=). */
export const ERNEST_TENANT_REQUEST_HEADER = 'x-ernest-tenant-id'

const AXA_INTEGRAL_CANONICAL_TENANT_ID = 'axa-integral'
/** Public `?tenant=` slug and in-app tenant id for Ernest Prévention (maquette v2). */
export const AXA_PREVENTION_CANONICAL_TENANT_ID = 'axa-prevention'
/** Legacy alias — normalized to {@link AXA_PREVENTION_CANONICAL_TENANT_ID}. */
export const AXA_PREVENTION_ALIAS_TENANT_ID = 'axa-prevention-v2'
const AXA_INTEGRAL_HOST_SUFFIX = 'axa-mon-compagnon-ernest.fr'

export function normalizeTenant(value: string | null | undefined): TenantId | null {
  if (!value) return null
  let normalized = String(value).trim().toLowerCase().replace(/\/+$/, "")
  if (normalized === AXA_PREVENTION_ALIAS_TENANT_ID) {
    normalized = AXA_PREVENTION_CANONICAL_TENANT_ID
  }
  if (normalized === 'axa-integrale') {
    normalized = 'axa-integral'
  }
  return normalized in TENANT_CONFIGS ? (normalized as TenantId) : null
}

/** Hostname + query (+ env) — safe on server and in middleware/proxy. */
export function resolveTenantIdForRequest(input: {
  hostname?: string | null
  tenantQuery?: string | null
  fallback?: TenantId
}): TenantId {
  const fallback = input.fallback ?? DEFAULT_TENANT
  const fromHost = tenantFromHostname(input.hostname)
  if (fromHost) return fromHost
  const fromQuery = normalizeTenant(input.tenantQuery)
  if (fromQuery) return fromQuery
  const fromEnv = normalizeTenant(process.env.NEXT_PUBLIC_TENANT)
  if (fromEnv) return fromEnv
  return fallback
}

export function resolveTenantId(fallback: TenantId = DEFAULT_TENANT): TenantId {
  if (typeof window === 'undefined') {
    return resolveTenantIdForRequest({ fallback })
  }

  const tenantFromHost = tenantFromHostname()
  if (tenantFromHost) {
    sessionStorage.setItem(TENANT_STORAGE_KEY, tenantFromHost)
    return tenantFromHost
  }

  const tenantFromUrl = normalizeTenant(new URLSearchParams(window.location.search).get('tenant'))
  if (tenantFromUrl) {
    sessionStorage.setItem(TENANT_STORAGE_KEY, tenantFromUrl)
    return tenantFromUrl
  }

  const tenantFromEnv = normalizeTenant(process.env.NEXT_PUBLIC_TENANT)
  if (tenantFromEnv) {
    sessionStorage.setItem(TENANT_STORAGE_KEY, tenantFromEnv)
    return tenantFromEnv
  }

  // Sans ?tenant= : tenant B2C par défaut (demo). Ne pas réutiliser un ancien slug en sessionStorage.
  sessionStorage.setItem(TENANT_STORAGE_KEY, fallback)
  return fallback
}

export function getTenantConfig(tenantId: string): TenantConfig {
  const normalized = normalizeTenant(tenantId)
  return TENANT_CONFIGS[normalized ?? DEFAULT_TENANT]
}

export function getTenantFlags(tenantId: string) {
  const id = normalizeTenant(tenantId) ?? DEFAULT_TENANT
  return {
    isAxaIntegralTenant: id === 'axa-integral',
    isAxaPreventionTenant: id === AXA_PREVENTION_CANONICAL_TENANT_ID,
    isErnestProTenant: id === 'ernest-pro',
    isEspritRetraiteTenant: id === 'esprit-retraite',
    isIrcemTenant: id === 'ircem',
    isKlesiaTenant: id === 'klesia',
    isMHTenant: id === 'mh',
    isUmenTenant: id === 'umen',
  }
}

/** AXA Prévention v2 dedicated host — tenant is implied without `?tenant=`. */
export function tenantFromHostname(hostname?: string | null): TenantId | null {
  const host = (
    (hostname ??
      (typeof window !== 'undefined' ? window.location.hostname : ''))
      .split(':')[0] ?? ''
  )
    .trim()
    .toLowerCase()
  if (!host) return null
  if (host === 'ernestprevention-axa.fr' || host.endsWith('.ernestprevention-axa.fr')) {
    return AXA_PREVENTION_CANONICAL_TENANT_ID
  }
  if (host === AXA_INTEGRAL_HOST_SUFFIX || host.endsWith(`.${AXA_INTEGRAL_HOST_SUFFIX}`)) {
    return AXA_INTEGRAL_CANONICAL_TENANT_ID
  }
  return null
}

/** Build-time / deploy default (`TENANT` → `NEXT_PUBLIC_TENANT` in compose). */
export function deploymentTenantId(): TenantId | null {
  return normalizeTenant(process.env.NEXT_PUBLIC_TENANT)
}

/** True when the hostname alone selects this tenant (no query param needed). */
export function tenantImpliedByHostname(
  tenantId: TenantId,
  hostname?: string | null,
): boolean {
  const fromHost = tenantFromHostname(hostname)
  return fromHost !== null && fromHost === tenantId
}

/** Skip `?tenant=` when hostname or deploy env already selects this tenant. */
export function tenantShouldOmitQueryParam(
  tenantId: TenantId,
  hostname?: string | null,
): boolean {
  if (tenantId === DEFAULT_TENANT) return true
  if (tenantImpliedByHostname(tenantId, hostname)) return true
  const deployment = deploymentTenantId()
  return deployment !== null && deployment === tenantId
}

export function syncTenantCookie(tenantId: TenantId): void {
  if (typeof document === 'undefined') return
  document.cookie = `${TENANT_STORAGE_KEY}=${tenantId}; path=/; max-age=31536000; SameSite=Lax`
}

/** DOM classes for tenant-scoped CSS (`tenant-axa-prevention` + legacy v2 alias class). */
export function tenantShellClassNames(tenantId: TenantId): string {
  const normalized = normalizeTenant(tenantId) ?? tenantId
  if (normalized === AXA_PREVENTION_CANONICAL_TENANT_ID) {
    return `tenant-${AXA_PREVENTION_CANONICAL_TENANT_ID} tenant-${AXA_PREVENTION_ALIAS_TENANT_ID}`
  }
  return `tenant-${normalized}`
}

export function appendTenantToPath(
  path: string,
  tenantId: TenantId,
  hostname?: string | null,
): string {
  if (tenantId === DEFAULT_TENANT) return path
  const host =
    hostname ?? (typeof window !== 'undefined' ? window.location.hostname : null)
  if (tenantShouldOmitQueryParam(tenantId, host)) return path
  const hashIndex = path.indexOf('#')
  const pathWithoutHash = hashIndex >= 0 ? path.slice(0, hashIndex) : path
  const hash = hashIndex >= 0 ? path.slice(hashIndex) : ''
  const [pathname, query = ''] = pathWithoutHash.split('?')
  const params = new URLSearchParams(query)
  params.set('tenant', tenantId)
  const queryString = params.toString()
  return `${pathname}?${queryString}${hash}`
}

export { TENANT_CONFIGS }
